import { Button, Input, Space, TableProps } from "antd";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DeleteIcon from "../../../../assets/icons/DeleteIcon";
import SearchIcon from "../../../../assets/icons/SearchIcon";
import CardTitle from "../../../../components/common/CardTitle";
import CustomCard from "../../../../components/common/CustomCard";
import ConfirmModal from "../../../../components/ui/ConfirmModal";
import CustomTable from "../../../../components/ui/CustomTable";
import { useGroupPermissionAssignMutation } from "../../../../features/administrative-setting/administrativeSetting";
import useError from "../../../../hooks/useError";
import useModal from "../../../../hooks/useModal";
import useTable from "../../../../hooks/useTable";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import { toast } from "../../../../lib/toast";
import {
  GetAllUsersDataParamsType,
  GetAllUsersDataType,
} from "../../../../types/portal/user";
import { countParPageSerialNumber } from "../../../../utils/number";
import { capitalize } from "../../../../utils/string";
import { addKeyInArray } from "../../../../utils/utils";
import AddUserModal from "./_components/AddUserModal";
import UserProductManage from "./_components/UserProductManage";
import AddUserIcon from "./_icons/AddUserIcon";
import { userPermissionTabList } from "./UserAddPermission";

const backLink = "/admin/user-access-permission/user-list";

type TList = {
  productId?: number;
  productName?: string;
  groupId?: number;
  groupName?: string;
  isEdit?: boolean;
};

type IUser = GetAllUsersDataType["data"]["users"];

enum Status {
  active = "status-success",
  inactive = "status-danger",
  invited = "status-info",
}

export default function UserBulkPermission() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState<TList[]>([]);
  const [selectedUser, setSelectedUser] = useState<IUser>([]);
  const { handleOpen, handleClose, open } = useModal<
    boolean | any,
    "CONFIRM_SUPER" | "CONFIRM_CREATE" | "ADD_USER"
  >();
  const { page, limit, handlePagination, handleLimit, keyword, handleSearch } =
    useTable<GetAllUsersDataParamsType>();
  const startIndex = (page - 1) * limit;
  const [groupPermissionAssign, { isLoading, isError, error, isSuccess }] =
    useGroupPermissionAssignMutation();
  useError(isError, error, "Group permission assign fail");

  const removeUser = (id: number) => {
    const filteredUser = selectedUser.filter((el) => el?.id !== id);
    setSelectedUser(filteredUser);
  };

  const onFinish = () => {
    if (!selectedUser.length) {
      toast("error", "You must choose at least one user");
      return;
    }

    if (!list.length) {
      toast("error", "You must choose at least one role group");
      return;
    }

    const newValues = {
      groupPermissionIds: list?.map((el) => el?.groupId),
      userIds: selectedUser?.map((el) => el?.id),
    };

    handleOpen({ type: "CONFIRM_CREATE", data: newValues });
  };

  const columns: TableProps<IUser[0]>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 40,
      render: (_, __, index) => countParPageSerialNumber(page, limit, index),
    },
    {
      title: "User Name",
      dataIndex: "username",
      render: (val: string) => val || "N/A",
    },
    {
      title: "Email",
      dataIndex: "email",
      render: (val: string) => val || "N/A",
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (val) => val?.name || "N/A",
    },
    {
      title: "Sub Department",
      dataIndex: "sub_department",
      render: (val) => val?.name || "N/A",
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (val: keyof typeof Status, record) => (
        <div className="flex items-center gap-2">
          <span className={Status[val]}>{capitalize(val || "-")}</span>
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "id",
      render: (val) => (
        <Button
          icon={<DeleteIcon color="#FF3838" />}
          className="action-button"
          size="small"
          onClick={() => removeUser(val)}
        />
      ),
    },
  ];

  // Filter by search
  const filteredUsers = selectedUser.filter((user) => {
    if (!keyword) return true;

    const search = keyword.toLowerCase();

    return (
      user.username?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search)
    );
  });

  useEffect(() => {
    if (isSuccess) {
      toast("success", "Group permission assigned successfully");
      navigate(backLink);
    }
  }, [isSuccess, navigate]);

  return (
    <SidebarLayout title="Bulk Permission" tabList={userPermissionTabList}>
      {open?.type === "CONFIRM_CREATE" && (
        <ConfirmModal
          confirmTitle="Confirm Action"
          questionText={`Are you sure you want to create ${
            selectedUser.length > 1 ? "these" : "this"
          } user${selectedUser.length > 1 ? "s" : ""}?`}
          okText="Yes, Confirmed"
          handleClose={handleClose}
          open={open?.open}
          okLoading={isLoading}
          handleOk={() => {
            groupPermissionAssign(open?.data);
          }}
        />
      )}

      {open?.type === "ADD_USER" && (
        <AddUserModal
          handleClose={handleClose}
          open={open?.open}
          data={selectedUser}
          setData={setSelectedUser}
        />
      )}

      <CustomCard className=" p-6 pb-0 rounded-b-none">
        <CardTitle>User Info</CardTitle>

        {selectedUser?.length ? (
          <div className="mt-6">
            <CustomTable
              loading={false}
              limit={limit}
              page={page}
              columns={columns}
              total={filteredUsers?.length}
              dataSource={addKeyInArray(
                filteredUsers?.slice(startIndex, startIndex + limit)
              )}
              handleLimit={handleLimit}
              handlePagination={handlePagination}
              filterSection={
                <Input
                  suffix={<SearchIcon />}
                  placeholder="Search by username, email"
                  style={{ width: 250 }}
                  allowClear
                  onChange={handleSearch}
                />
              }
            />
          </div>
        ) : null}

        <div className="my-6">
          <Button
            className="modal-button flex items-center"
            type="primary"
            htmlType="button"
            size="middle"
            icon={<AddUserIcon />}
            onClick={() => handleOpen({ type: "ADD_USER" })}
          >
            Add User
          </Button>
        </div>

        <CardTitle>User Role Group</CardTitle>

        <UserProductManage list={list} setList={setList} />

        <Space className="w-full my-4" size={12}>
          <Link to={backLink}>
            <Button className="normal-dark-button modal-button" size="middle">
              Cancel
            </Button>
          </Link>
          <Button
            className="modal-button"
            type="primary"
            htmlType="submit"
            size="middle"
            // loading={id ? updateSingleUserIsLoading : isCreateUserLoading}
            onClick={onFinish}
          >
            {id ? "Update" : "Create user"}
          </Button>
        </Space>
      </CustomCard>
    </SidebarLayout>
  );
}
